import { useContext, useEffect, useRef, useState } from "react";
import GoogleDocViewer from "../Doc";
import Certification from "../Certification";
import MCQPractice from "../MCQ";
import { FcLock } from "react-icons/fc";
import { HiMiniLockOpen } from "react-icons/hi2";
import { HashLoader } from "react-spinners";
import { Link, useParams } from "react-router-dom";
import { MyContext } from "../../context/context";
import usePreserveScroll from "../usePreserveScroll";
import "./sessions.css";

const SessionsData = () => {
  const { gc } = useParams();
  const {
    storeCourseData,
    storedCourse,
    setShowSubmissionForm,
    submitProjectId,
  } = useContext(MyContext);
  const activeGC = gc?.toUpperCase() ?? "";
  const [activeSection, setActiveSection] = useState("classes");

  const [activeCheatSheet, setActiveCheatSheet] = useState("NA");
  const [activeCodingCheatSheet, setActiveCodingCheatSheet] = useState("NA");

  const [gcData, setGcData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [mcqOrder, setMcqOrder] = useState("");

  const [user] = useState(() => {
    const stored = localStorage.getItem("student");
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    if (!gc || !user) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/cheat-sheet`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              course: user?.student?.program,
              gc: activeGC,
              uid: user?.student?.UID,
              joinedDate: user?.student?.joinedDate,
            }),
          },
        );

        const result = await response.json();
        setGcData(result.data || {});
        storeCourseData(result.data || {});
      } catch (err) {
        throw new Error();
      } finally {
        setIsLoading(false);
      }
    };

    // fetch only when storedCourse object is empty
    if (Object.keys(storedCourse).length === 0) {
      fetchData();
    }
  }, []);

  const scrollRef = useRef(null);
  usePreserveScroll(scrollRef, `scroll-${activeGC}-${activeSection}`);

  // ---------------------------------------------
  // Time period mapping
  // ---------------------------------------------
  const getTimePeriod = (program) => {
    const techProMap = {
      GC1: "Static Website Designing (HTML, CSS, Bootstrap)",
      GC2: "Responsive Website Designing (HTML, CSS, Bootstrap)",
      GC3: "Python Programming Language",
      GC4: "Dynamic Website Designing (HTML, CSS, Bootstrap, JavaScript)",
      GC5: "Javascript Essentials, CSS Flexbox & Developer Foundations (GIT, COMMANDLINE)",
      GC6: "Node JS, Introduction to Database (SQL) & MongoDB",
      GC7: "React JS",
      GC8: "Build your own full stack web application",
      GC9: "Gen AI & Data Science Fundamentals",
    };

    const techSmartMap = {
      GC1: "Static Website Designing (HTML, CSS, Bootstrap)",
      GC2: "Responsive Website Designing (HTML, CSS, Bootstrap)",
      GC3: "Dynamic Website Designing (HTML, CSS, Bootstrap, JavaScript)",
      GC4: "Javascript Essentials, CSS Flexbox & Developer Foundations (GIT, COMMANDLINE)",
      GC5: "Node JS, Introduction Database(SQL) & MongoDB",
      GC6: "React JS",
      GC7: "Build your own full stack web application",
    };

    return program === "tech-pro"
      ? techProMap[activeGC]
      : techSmartMap[activeGC];
  };

  const handleSubmissionForm = (id) => {
    setShowSubmissionForm(true);
    submitProjectId(id);
  };
  const getButtons = (item) => {
    if (item.status === "locked") return null;

    switch (item.tag) {
      case "class":
        return (
          <>
            <a href={item.link} target="_blank" rel="noreferrer">
              <button className="view-btn">View Session</button>
            </a>

            {item.cheatSheet !== "NA" && (
              <Link to={`/doc/${item.id}`}>
                <button className="start-btn">View Cheat Sheet</button>
              </Link>
            )}
          </>
        );

      case "mcq":
        return (
          <Link className="gc-link" to={`/${gc}/mcq/${item.link}`}>
            <button
              className="view-btn"
              onClick={() => setMcqOrder(item.order)}
            >
              Take MCQ Test
            </button>
          </Link>
        );

      // Kareem Developed code
      case "coding":
        return (
          <>
            {/* <Link to={`/project/${item.id}`}>
            <button className="view-btn">Video Explanation</button>
          </Link> */}

            <Link to={`/doc/${item.id}`}>
              <button className="start-btn">Instructions</button>
            </Link>

            {item.codeSol && (
              <Link to={`/doc/${item.id}`}>
                <button className="view-solution"> Solution</button>
              </Link>
            )}
            <br />
            <button
              className="start-btn"
              onClick={() => {
                handleSubmissionForm(item.projectId);
              }}
            >
              Submit
            </button>
          </>
        );

      case "tour":
        return (
          <>
            <Link to={`/project/${item.id}`}>
              <button className="view-btn">Portal Tour</button>
            </Link>
          </>
        );

      case "session":
        return (
          <>
            <Link to={`/project/${item.id}`}>
              <button className="view-btn">View Session</button>
            </Link>
          </>
        );

      default:
        return null;
    }
  };

  const currentData = storedCourse?.[activeGC]?.[activeSection] || []; // safe fallback for empty data

  return (
    <div className="main-container">
      {/* MAIN CONTENT (only when not MCQ/coding/certification page) */}
      {activeGC && !["MCQ", "CODING", "CERTIFICATION"].includes(activeGC) && (
        <main className="main-content">
          <div className="content-header">
            <h2>{getTimePeriod(user?.student?.program)}</h2>

            <div className="breadcrumb">
              <span>
                {user?.student?.program === "tech-pro"
                  ? "Tech Pro"
                  : "Tech Smart"}
              </span>{" "}
              / <span>{activeGC}</span> /{" "}
              <span>
                {activeSection === "classes" ? "Classes" : "Coding Practices"}
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-cont">
              <HashLoader color="#4582b0" />
            </div>
          ) : (
            <div className="content-grid" ref={scrollRef}>
              {currentData.length > 0 ? (
                currentData.map((item) => (
                  <div key={item.id} className="content-card">
                    {/* Tags */}
                    {item.tag === "mcq" && (
                      <div className="new-ribbon">MCQ</div>
                    )}
                    {item.tag === "coding" && (
                      <div className="new-ribbon">Coding</div>
                    )}
                    {item.tag === "class" && (
                      <div className="new-ribbon">Session</div>
                    )}
                    {item.tag === "session" && (
                      <div className="new-ribbon">Session</div>
                    )}

                    {item.tag === "tour" && (
                      <div className="new-ribbon">Tour</div>
                    )}

                    {/* Header */}
                    <div className="card-header">
                      <h3>{item.title}</h3>

                      <div className="card-meta">
                        {item.duration && (
                          <span className="meta-item">⏱️ {item.duration}</span>
                        )}
                        {item.level && (
                          <span
                            className={`level-badge ${item.level.toLowerCase()}`}
                          >
                            {item.level}
                          </span>
                        )}{" "}
                        {item.status === "locked" ? (
                          <FcLock className="locked-icon" />
                        ) : (
                          <HiMiniLockOpen className="unlock-icon" />
                        )}
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="card-actions">{getButtons(item)}</div>
                  </div>
                ))
              ) : (
                <div className="no-content">
                  <div className="no-content-icon">📝</div>
                  <h3>
                    <FcLock /> Content Currently Locked
                  </h3>
                </div>
              )}
            </div>
          )}
        </main>
      )}

      {/* OTHER SPECIAL PAGES */}
      {activeGC === "MCQ" && (
        <MCQPractice activeGC={activeGC} mcqOrder={mcqOrder} />
      )}

      {activeGC === "CODING" && (
        <GoogleDocViewer docUrl={activeCodingCheatSheet} />
      )}

      {activeGC === "CLASS" && <GoogleDocViewer docUrl={activeCheatSheet} />}

      {activeGC === "CERTIFICATION" && <Certification />}
    </div>
  );
};

export default SessionsData;
